import { useBlockTooltip } from '../common/hooks/useBlockTooltip';
import { dynamic } from '@unifyapps/carbon/external/dynamic';
import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import useBlockEvents from '@unifyapps/carbon/no-code/hooks/useBlockEvents';
import { isEmptyBlock } from '@unifyapps/carbon/no-code/utils/block';
import type { CardBlockStateType, CardComponentType } from '@unifyapps/defs/types/blocks';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import useForkRef from '@unifyapps/ui/hooks/useForkRef';
import { memo, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { useBlockInteractions } from '../hooks/useBlockInteractions';
import { CardBuilderCollapsibleSync } from './CardBuilderCollapsibleSync';
import { CardLayout, CardLayoutSlots } from './CardLayout';
import { useCollapsibleParams } from './hooks/useCollapsibleParams';
import type { CardRef } from './types';

const DraggableBlockProvider = dynamic(
  () =>
    import(
      /* webpackChunkName: "draggable-block-provider" */
      '../common/components/DraggableBlockProvider'
    ).then((module) => module.DraggableBlockProvider),
  { loading: () => null },
);

function Card({
  blockState,
  attributesAndEventListeners,
  events,
  component,
  block,
  updateBlockState,
  blockRef: _blockRef,
  dndRef,
  computedBlockState,
  registry,
}: BlockComponentProps<CardComponentType, CardBlockStateType, CardBlockStateType>) {
  const blockRef = useForkRef(_blockRef, dndRef);

  const BlockRenderer = registry.getBlockRenderer();

  const { content, appearance } = computedBlockState;
  const { slots } = component;

  const { emitEvent } = useBlockEvents(events, computedBlockState.id);

  const { onClick, onMouseEnter, longPressProps } = useBlockInteractions({
    events,
    emitEvent,
    blockType: 'Card',
  });

  const setExpandedRef = useRef<((expanded: boolean) => void) | null>(null);
  const isProgrammaticCollapseChangeRef = useRef(false);

  const handleSetExpandedRef = useCallback((setExpanded: (expanded: boolean) => void) => {
    setExpandedRef.current = setExpanded;
  }, []);

  const { collapsibleParams } = useCollapsibleParams({
    blockState,
    updateBlockState,
  });

  // track previous collapse state to emit events on change
  const prevIsCollapsedRef = useRef<boolean | undefined>(undefined);

  // emit collapse/expand events when state changes
  useEffect(() => {
    if (!collapsibleParams.enabled) {
      return;
    }

    const prevIsCollapsed = prevIsCollapsedRef.current;
    const currentIsCollapsed = collapsibleParams.isCollapsed;

    // only emit on actual state change (not on initial render)
    if (prevIsCollapsed !== undefined && prevIsCollapsed !== currentIsCollapsed) {
      if (currentIsCollapsed) {
        void emitEvent({ eventType: EventTypeEnum.OnCollapse });
      } else {
        void emitEvent({ eventType: EventTypeEnum.OnExpand });
      }
      void emitEvent({
        eventType: EventTypeEnum.OnToggle,
        eventPayload: { isCollapsed: currentIsCollapsed, blockId: computedBlockState.id },
      });
    }

    prevIsCollapsedRef.current = currentIsCollapsed;
  }, [collapsibleParams.isCollapsed, collapsibleParams.enabled, emitEvent, computedBlockState.id]);

  useImperativeHandle(
    _blockRef,
    (): CardRef => ({
      setOrToggleCard: (newState?: boolean) => {
        // newState: true = expanded, false = collapsed, undefined = toggle
        const targetExpanded = newState !== undefined ? newState : collapsibleParams.isCollapsed;

        // update blockState
        collapsibleParams.setIsCollapsed(!targetExpanded);
        // update useCollapse hook state
        setExpandedRef.current?.(targetExpanded);
      },
    }),
    [collapsibleParams],
  );

  const tooltipSlotBlockId = slots?.tooltip?.blockId;

  const cardContent = (
    <CardLayout
      appearance={appearance}
      attributesAndEventListeners={attributesAndEventListeners}
      block={block}
      blockRef={blockRef}
      collapsibleParams={collapsibleParams}
      hasClickEvent={Boolean(onClick)}
      isProgrammaticCollapseChangeRef={isProgrammaticCollapseChangeRef}
      longPressProps={longPressProps}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onSetExpandedRef={handleSetExpandedRef}
    >
      <CardLayout.Slot name={CardLayoutSlots.Header}>
        {slots?.header?.blockId && !isEmptyBlock(slots.header.blockId) ? (
          <BlockRenderer
            blockId={slots.header.blockId}
            key={slots.header.blockId}
            parentBlockId={blockState.id}
          />
        ) : null}
      </CardLayout.Slot>
      <CardLayout.Slot name={CardLayoutSlots.Body}>
        {slots?.body?.blockId && !isEmptyBlock(slots.body.blockId) ? (
          <BlockRenderer blockId={slots.body.blockId} parentBlockId={blockState.id} />
        ) : null}
      </CardLayout.Slot>
      <CardLayout.Slot name={CardLayoutSlots.Footer}>
        {slots?.footer?.blockId && !isEmptyBlock(slots.footer.blockId) ? (
          <BlockRenderer blockId={slots.footer.blockId} parentBlockId={blockState.id} />
        ) : null}
      </CardLayout.Slot>
    </CardLayout>
  );

  const { wrapWithTooltip, isBuilder, tooltipBuilderNode } = useBlockTooltip({
    blockId: blockState.id,
    tooltipAddOn: content.addOns?.tooltip,
    tooltipSlotBlockId,
    BlockRenderer,
  });

  const finalCardContent = (
    <>
      {wrapWithTooltip(cardContent)}
      {isBuilder ? (
        <>
          {tooltipBuilderNode}
          <CardBuilderCollapsibleSync
            blockState={blockState}
            isProgrammaticCollapseChangeRef={isProgrammaticCollapseChangeRef}
            setExpandedRef={setExpandedRef}
            updateBlockState={updateBlockState}
          />
        </>
      ) : null}
    </>
  );

  const isDraggable = content.addOns?.draggable?.data;

  if (isDraggable) {
    return (
      <DraggableBlockProvider blockId={blockState.id} draggable={content.addOns?.draggable}>
        {finalCardContent}
      </DraggableBlockProvider>
    );
  }

  return finalCardContent;
}

export default memo(Card);
